import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Users,
  FileText,
  Settings,
  ClipboardList,
  Wrench,
  Package,
  DollarSign,
  CheckCircle,
  UserCheck,
  Building,
  Calendar,
  PackageSearch,
  Award,
  FileBarChart,
  Receipt,
  Shield,
} from 'lucide-react';

const sidebarItems = {
  admin: [
    { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
    { name: 'Specialists', href: '/admin/specialists', icon: UserCheck },
    { name: 'Qualifications', href: '/admin/qualifications', icon: Award },
    { name: 'PPM', href: '/admin/ppm', icon: ClipboardList },
    { name: 'Users', href: '/admin/users', icon: Users },
    { name: 'Activity Logs', href: '/admin/activity-logs', icon: FileBarChart },
    { name: 'Reconciliation', href: '/admin/reconciliation', icon: Receipt },
    { name: 'GDPR', href: '/admin/gdpr', icon: Shield },
    { name: 'Settings', href: '/admin/settings', icon: Settings },
  ],
  manager: [
    { name: 'Dashboard', href: '/manager', icon: LayoutDashboard },
    { name: 'Sites', href: '/manager/sites', icon: Building },
    { name: 'Jobs', href: '/manager/jobs', icon: Wrench },
    { name: 'Calendar', href: '/manager/calendar', icon: Calendar },
    { name: 'Invoices', href: '/manager/invoices', icon: DollarSign },
    { name: 'Spend', href: '/manager/finance', icon: Receipt },
    { name: 'Team', href: '/manager/team', icon: Users },
    { name: 'Assets', href: '/manager/assets', icon: Package },
    { name: 'Compare Sites', href: '/manager/compare', icon: FileBarChart },
    { name: 'Reports', href: '/manager/reports', icon: FileBarChart },
    { name: 'Settings', href: '/manager/settings', icon: Settings },
  ],
  specialist: [
    { name: 'Dashboard', href: '/specialist', icon: LayoutDashboard },
    { name: 'Jobs', href: '/specialist/jobs', icon: Wrench },
    { name: 'PPM', href: '/specialist/ppm', icon: ClipboardList },
    { name: 'Inventory', href: '/specialist/inventory', icon: PackageSearch },
    { name: 'Profile', href: '/specialist/profile', icon: UserCheck },
    { name: 'Earnings', href: '/specialist/earnings', icon: DollarSign },
    { name: 'Reports', href: '/specialist/reports', icon: FileBarChart },
  ],
  partner: [
    { name: 'Dashboard', href: '/partner', icon: LayoutDashboard },
    { name: 'Assets', href: '/partner/assets', icon: Package },
    { name: 'Jobs', href: '/partner/jobs', icon: Calendar },
    { name: 'PPM', href: '/partner/ppm', icon: ClipboardList },
    { name: 'Invoices', href: '/partner/invoices', icon: DollarSign },
    { name: 'Reports', href: '/partner/reports', icon: FileBarChart },
    { name: 'Settings', href: '/partner/settings', icon: Settings },
  ],
};

export const Sidebar: React.FC = () => {
  const { user, userRole } = useAuth();
  const [open, setOpen] = useState(false);

  if (!user || !userRole) {
    return null;
  }

  const items = sidebarItems[userRole] || [];

  const NavItems = () => (
    <nav className="p-4 space-y-2">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.href}
            to={item.href}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              cn(
                'flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors persi-animate',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              )
            }
          >
            <Icon className="h-4 w-4" />
            <span>{item.name}</span>
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <>
      {/* Mobile Menu Toggle */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild className="lg:hidden">
          <Button 
            variant="outline" 
            size="icon"
            className="fixed top-3 left-3 z-[60] shadow-lg bg-card"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-64 p-0 z-[100] bg-card">
          <div className="pt-4">
            <NavItems />
          </div>
        </SheetContent>
      </Sheet>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-64 bg-card border-r shadow-sm shrink-0">
        <NavItems />
      </aside>
    </>
  );
};